/**
 * Web 前端用户行为埋点
 *
 * 模块说明：
 * 本模块用于 **Web 管理后台前端** 的用户行为追踪和上报。
 *
 * 功能：
 * 1. 自动追踪页面访问（PV）
 * 2. 自动追踪路由变化
 * 3. 提供手动埋点 API（点击、表单提交、搜索等）
 * 4. 页面停留时长统计
 * 5. 批量上报和缓存
 *
 * @see api/modules/web-logs.ts - Web 前端日志 API
 * @see modules/mes/web-monitoring/ - Web 日志查看界面
 * @author YSFL Team
 * @date 2026-01-29
 */
import type { RouteLocationNormalized, Router } from 'vue-router';

import type {
  WebBehaviorActionResult as ActionResult,
  WebBehaviorActionType as ActionType,
  WebBehaviorLogForm as BehaviorLogForm,
} from '#/api/modules/web-logs';

import { useUserStore } from '@vben/stores';

import { getDeviceId, getSessionId } from '#/utils/device';
import { sendLogs, sendLogsKeepalive } from '#/utils/log-service-config';
import { updateConfigFromResponse } from '#/utils/monitoring-config';

// ========================================================================
// 认证错误检测
// ========================================================================

/**
 * 检查是否为认证错误（401）
 * 用于在上报失败时判断是否需要停止定时器
 */
function isAuthError(error: any): boolean {
  if (error?.response?.status === 401) return true;
  if (error?.message?.includes('Unauthenticated')) return true;
  if (error?.response?.data?.message?.includes('Unauthenticated')) return true;
  return false;
}

// ========================================================================
// 类型定义
// ========================================================================

export type { ActionResult, ActionType, BehaviorLogForm };

/** 配置选项 */
interface BehaviorTrackerOptions {
  /** 是否启用 */
  enabled?: boolean;
  /** 批量上报阈值 */
  batchSize?: number;
  /** 最大缓存数 */
  maxCacheSize?: number;
  /** 上报间隔（毫秒） */
  reportInterval?: number;
  /** 是否在控制台输出 */
  showInConsole?: boolean;
  /** 需要忽略的路由前缀 */
  ignoredRoutes?: string[];
}

// ========================================================================
// 配置
// ========================================================================

const defaultOptions: Required<BehaviorTrackerOptions> = {
  enabled: import.meta.env.PROD, // 生产环境默认启用
  batchSize: 10,
  maxCacheSize: 200,
  reportInterval: 30_000, // 30秒
  showInConsole: import.meta.env.DEV,
  ignoredRoutes: ['/auth', '/login', '/redirect'],
};

let options: Required<BehaviorTrackerOptions> = { ...defaultOptions };

// 防止重复初始化
let isInitialized = false;

// 待上报的行为日志
const pendingLogs: BehaviorLogForm[] = [];

// 缓存的设备信息
let cachedDeviceId: null | string = null;
let cachedSessionId: null | string = null;

// 当前页面信息（用于计算停留时长）
let currentPageInfo: null | {
  enterTime: number;
  route: string;
  title: string;
} = null;

// 上报定时器
let reportTimer: null | ReturnType<typeof setInterval> = null;

// 全局点击委托监听器（销毁时移除）
let globalClickListener: ((event: MouseEvent) => void) | null = null;

// 上一次上报的点击指纹（同目标 1 秒内去重）
let lastClickFingerprint = '';
let lastClickTime = 0;

// ========================================================================
// 工具函数
// ========================================================================

/**
 * 格式化本地时间
 */
function formatLocalDateTime(date: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** 客户端幂等键（uuid v4；旧浏览器无 crypto.randomUUID 时随机回退）。 */
function newClientEventId(): string {
  const cryptoObj = globalThis.crypto;
  if (typeof cryptoObj?.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replaceAll(/[xy]/g, (ch) => {
    const rand = Math.trunc(Math.random() * 16);
    const value = ch === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

/**
 * 获取当前用户信息
 */
function getCurrentUser(): {
  realName: null | string;
  userId: null | number;
  username: null | string;
} {
  try {
    const userStore = useUserStore();
    const userInfo = userStore.userInfo as any;
    const username = userInfo?.username || null;
    const realName =
      userInfo?.realName || userInfo?.name || userInfo?.nickname || null;

    return {
      userId: userInfo?.id || null,
      username: username || realName,
      realName,
    };
  } catch {
    return { userId: null, username: null, realName: null };
  }
}

/**
 * 检查用户是否已登录
 */
function isUserLoggedIn(): boolean {
  try {
    const userStore = useUserStore();
    return !!userStore.userInfo?.id;
  } catch {
    return false;
  }
}

/**
 * 获取设备ID（带缓存）
 */
async function getDeviceIdCached(): Promise<string> {
  if (!cachedDeviceId) {
    cachedDeviceId = await getDeviceId();
  }
  return cachedDeviceId;
}

/**
 * 获取会话ID（带缓存）
 */
function getSessionIdCached(): string {
  if (!cachedSessionId) {
    cachedSessionId = getSessionId();
  }
  return cachedSessionId;
}

/**
 * 获取浏览器信息
 */
function getBrowserInfo(): { browser: string; version: string } {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let version = '';

  if (ua.includes('Chrome') && !ua.includes('Edge')) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Edge')) {
    browser = 'Edge';
    version = ua.match(/Edge\/(\d+)/)?.[1] || '';
  }

  return { browser, version };
}

/**
 * 获取操作系统
 */
function getOS(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

/**
 * 从路由提取业务模块名
 */
function extractBusinessModule(route: string): string {
  // 示例路由：/system/web-monitoring/dashboard -> web-monitoring
  // 示例路由：/material/list -> material
  const parts = route.split('/').filter(Boolean);
  if (parts.length >= 2) {
    return parts[1] || parts[0] || 'unknown';
  }
  return parts[0] || 'unknown';
}

function safePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

function routeQueryKeys(route: RouteLocationNormalized): string[] {
  return Object.keys(route.query || {});
}

/**
 * 检查是否应该忽略该路由
 */
function shouldIgnoreRoute(route: string): boolean {
  return options.ignoredRoutes.some((prefix) => route.startsWith(prefix));
}

// ========================================================================
// 核心功能
// ========================================================================

/**
 * 创建行为日志
 */
async function createBehaviorLog(
  actionType: ActionType,
  actionName: string,
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    duration?: number;
    fromPage?: string;
    result?: ActionResult;
    stayDuration?: number;
    targetElement?: string;
    targetId?: string;
    targetText?: string;
    toPage?: string;
  },
): Promise<BehaviorLogForm> {
  const deviceId = await getDeviceIdCached();
  const sessionId = getSessionIdCached();
  const browserInfo = getBrowserInfo();
  const currentUser = getCurrentUser();
  const route = window.location.pathname;
  const context = {
    user: {
      username: currentUser.username,
      realName: currentUser.realName,
    },
    routeName: window.history.state?.current || route,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    ...extra?.context,
  };

  return {
    device_id: deviceId,
    // 客户端幂等键：失败重放/卸载重发时后端按 (device_id, client_event_id) 去重
    client_event_id: newClientEventId(),
    session_id: sessionId,
    user_id: currentUser.userId,
    username: currentUser.username,
    browser: browserInfo.browser,
    browser_version: browserInfo.version,
    os: getOS(),
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    action_type: actionType,
    action_name: actionName,
    business_module: extra?.businessModule || extractBusinessModule(route),
    page_url: safePageUrl(),
    page_route: route,
    page_title: document.title || null,
    target_element: extra?.targetElement,
    target_id: extra?.targetId,
    target_text: extra?.targetText,
    action_result: extra?.result || 'success',
    action_duration: extra?.duration,
    from_page: extra?.fromPage,
    to_page: extra?.toPage,
    referrer: document.referrer || null,
    stay_duration: extra?.stayDuration,
    context,
    occurred_at: formatLocalDateTime(new Date()),
  };
}

/**
 * 收集行为日志
 */
async function collectBehavior(
  actionType: ActionType,
  actionName: string,
  extra?: Parameters<typeof createBehaviorLog>[2],
): Promise<void> {
  // 忽略未登录用户的行为
  if (!isUserLoggedIn()) {
    return;
  }

  const log = await createBehaviorLog(actionType, actionName, extra);

  if (options.showInConsole) {
    // eslint-disable-next-line no-console
    console.info('[BehaviorTracker]', actionType, actionName, log);
  }

  // 添加到队列
  pendingLogs.push(log);

  // 队列满时立即上报
  if (pendingLogs.length >= options.batchSize) {
    flushLogs();
  }

  // 限制队列大小
  if (pendingLogs.length > options.maxCacheSize) {
    pendingLogs.splice(0, pendingLogs.length - options.maxCacheSize);
  }
}

/**
 * 批量上报行为日志
 */
async function flushLogs(): Promise<void> {
  if (pendingLogs.length === 0 || !options.enabled) {
    return;
  }

  // 检查登录状态
  if (!isUserLoggedIn()) {
    if (options.showInConsole) {
      // eslint-disable-next-line no-console
      console.info('[BehaviorTracker] 用户未登录，跳过上报并清空缓存');
    }
    pendingLogs.length = 0;
    return;
  }

  // 检查网络状态
  if (!navigator.onLine) {
    if (options.showInConsole) {
      // eslint-disable-next-line no-console
      console.info('[BehaviorTracker] 网络离线，跳过上报');
    }
    return;
  }

  const logsToReport = pendingLogs.splice(0, options.batchSize);

  try {
    // 使用配置化的日志服务 URL
    const result = await sendLogs('behavior', logsToReport);

    if (result) {
      // 处理响应中的监控配置
      if (result?.data?.monitoringConfig) {
        await updateConfigFromResponse(result.data.monitoringConfig);
      }
    } else {
      // 上报失败，放回队列
      pendingLogs.unshift(...logsToReport);
    }
  } catch (error: any) {
    // 检测认证错误，如果登录失效则停止定时上报
    if (isAuthError(error)) {
      console.warn('[BehaviorTracker] 检测到认证失败，停止定时上报');
      destroyBehaviorTracker();
      return;
    }
    // 其他错误，上报失败，放回队列
    pendingLogs.unshift(...logsToReport);
  }
}

/**
 * 记录页面进入
 */
function recordPageEnter(route: RouteLocationNormalized): void {
  if (shouldIgnoreRoute(route.path)) {
    return;
  }

  // 记录当前页面信息
  currentPageInfo = {
    route: route.path,
    title: (route.meta?.title as string) || document.title || '',
    enterTime: Date.now(),
  };

  // 记录页面访问
  collectBehavior('page_view', currentPageInfo.title || route.path, {
    businessModule: extractBusinessModule(route.path),
    context: {
      queryKeys: routeQueryKeys(route),
      params: route.params,
      routeName: route.name,
    },
  });
}

/**
 * 记录页面离开
 */
function recordPageLeave(
  from: RouteLocationNormalized,
  to: RouteLocationNormalized,
): void {
  if (!currentPageInfo || shouldIgnoreRoute(from.path)) {
    return;
  }

  const stayDuration = Date.now() - currentPageInfo.enterTime;

  // 只记录停留超过1秒的页面
  if (stayDuration > 1000) {
    collectBehavior('page_leave', currentPageInfo.title || from.path, {
      businessModule: extractBusinessModule(from.path),
      fromPage: from.path,
      toPage: to.path,
      stayDuration,
    });
  }
}

// ========================================================================
// 路由监听
// ========================================================================

/**
 * 设置路由监听
 */
function setupRouterGuard(router: Router): void {
  router.beforeEach((to, from) => {
    // 记录离开页面
    if (from.path !== '/' && from.path !== to.path) {
      recordPageLeave(from, to);
    }
    return true;
  });

  router.afterEach((to, from) => {
    // 记录进入页面
    if (to.path !== from.path) {
      recordPageEnter(to);
    }
  });
}

// ========================================================================
// 全局点击委托
// ========================================================================

/** 可交互元素选择器：按钮/链接/单选复选/带角色按钮的元素/各类可点组件 */
const CLICKABLE_SELECTOR = [
  'a',
  'button',
  'input[type="button"]',
  'input[type="checkbox"]',
  'input[type="radio"]',
  'input[type="submit"]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="switch"]',
  '.ant-btn',
  '.ant-dropdown-menu-item',
  '.ant-menu-item',
  '.ant-select-item',
  '.ant-tabs-tab',
].join(',');

/** 截断文本，避免把长文本写入日志 */
function truncateClickText(text: null | string | undefined): null | string {
  if (!text) return null;
  const trimmed = text.trim().replaceAll(/\s+/g, ' ');
  return trimmed ? truncateUtf16(trimmed, 50) : null;
}

/** 按 Unicode 码点截断，避免截断 emoji/中文代理对 */
function truncateUtf16(text: string, maxLength: number): string {
  return [...text].length <= maxLength
    ? text
    : [...text].slice(0, maxLength).join('');
}

/**
 * 从点击目标向上查找最近的可交互元素并提取埋点信息
 * 找不到可交互元素（点击的是纯文本/表格行等）时返回 null
 */
function resolveClickTarget(target: EventTarget | null | undefined): null | {
  element: string;
  id: null | string;
  text: null | string;
} {
  if (!(target instanceof Element)) return null;

  const clickable = target.closest(CLICKABLE_SELECTOR);
  if (!clickable) return null;

  const element =
    clickable instanceof HTMLElement
      ? clickable.tagName.toLowerCase()
      : (clickable.getAttribute('role') ?? clickable.tagName.toLowerCase());

  const text =
    truncateClickText(
      clickable.getAttribute('aria-label') ||
        (clickable as HTMLElement).innerText ||
        clickable.getAttribute('title'),
    ) ?? truncateClickText(clickable.textContent);

  return {
    element,
    id: clickable.id || null,
    text,
  };
}

/**
 * 设置全局点击委托监听
 *
 * 说明：document 级捕获监听，自动覆盖所有模块的按钮/链接/菜单等可交互元素，
 * 替代逐页手动接入 trackClick。纯文本/表格行点击不上报，避免噪声。
 */
function setupGlobalClickListener(): void {
  if (globalClickListener) return;

  globalClickListener = (event: MouseEvent) => {
    if (!options.enabled) return;
    // 忽略非主键点击与登录相关页面
    if (event.button !== 0) return;
    if (shouldIgnoreRoute(window.location.pathname)) return;

    const target = resolveClickTarget(event.target);
    if (!target) return;

    // 同一目标 1 秒内去重（快速连点只记首击）
    const fingerprint = `${target.element}|${target.id || ''}|${target.text || ''}`;
    const now = Date.now();
    if (fingerprint === lastClickFingerprint && now - lastClickTime < 1000) {
      return;
    }
    lastClickFingerprint = fingerprint;
    lastClickTime = now;

    collectBehavior('click', target.text || target.element, {
      targetElement: target.element,
      targetId: target.id ?? undefined,
      targetText: target.text ?? undefined,
    });
  };

  document.addEventListener('click', globalClickListener, true);
}

function removeGlobalClickListener(): void {
  if (globalClickListener) {
    document.removeEventListener('click', globalClickListener, true);
    globalClickListener = null;
  }
}

// ========================================================================
// 初始化
// ========================================================================

/**
 * 初始化行为追踪
 */
export function setupBehaviorTracker(
  router: Router,
  customOptions?: BehaviorTrackerOptions,
): void {
  // 防止重复初始化
  if (isInitialized) {
    // eslint-disable-next-line no-console
    console.info('[BehaviorTracker] 已初始化，跳过重复初始化');
    return;
  }

  // 合并配置
  options = { ...defaultOptions, ...customOptions };

  // 标记已初始化
  isInitialized = true;

  // 设置路由监听
  setupRouterGuard(router);

  // 设置全局点击委托
  setupGlobalClickListener();

  // 设置定时上报
  reportTimer = setInterval(() => {
    flushLogs();
  }, options.reportInterval);

  // 页面卸载时上报：fetch keepalive 可携带 Authorization（身份随批落库），
  // 失败才回退 sendBeacon（无法带认证头，匿名落库）。
  window.addEventListener('beforeunload', () => {
    if (
      pendingLogs.length > 0 &&
      options.enabled &&
      navigator.onLine &&
      isUserLoggedIn()
    ) {
      void sendLogsKeepalive('behavior', [...pendingLogs]);
    }
  });

  // eslint-disable-next-line no-console
  console.info('[BehaviorTracker] Web端行为追踪已初始化', {
    enabled: options.enabled,
    batchSize: options.batchSize,
  });
}

// ========================================================================
// 公开 API - 手动埋点
// ========================================================================

/**
 * 追踪点击事件
 */
export function trackClick(
  elementName: string,
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    targetId?: string;
    targetText?: string;
  },
): void {
  collectBehavior('click', elementName, {
    targetElement: elementName,
    ...extra,
  });
}

/**
 * 追踪表单提交
 */
export function trackFormSubmit(
  formName: string,
  result: ActionResult = 'success',
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    duration?: number;
  },
): void {
  collectBehavior('form_submit', formName, {
    result,
    ...extra,
  });
}

/**
 * 追踪搜索
 */
export function trackSearch(
  keyword: string,
  resultCount?: number,
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
  },
): void {
  collectBehavior('search', keyword, {
    context: {
      keyword,
      resultCount,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪导出操作
 */
export function trackExport(
  exportType: string,
  result: ActionResult = 'success',
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    duration?: number;
    recordCount?: number;
  },
): void {
  collectBehavior('export', exportType, {
    result,
    context: {
      recordCount: extra?.recordCount,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪下载操作
 */
export function trackDownload(
  fileName: string,
  result: ActionResult = 'success',
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    fileSize?: number;
  },
): void {
  collectBehavior('download', fileName, {
    result,
    context: {
      fileSize: extra?.fileSize,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪打印操作
 */
export function trackPrint(
  printType: string,
  result: ActionResult = 'success',
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
  },
): void {
  collectBehavior('print', printType, {
    result,
    ...extra,
  });
}

/**
 * 追踪 Tab 切换
 */
export function trackTabSwitch(
  tabName: string,
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
    fromTab?: string;
  },
): void {
  collectBehavior('tab_switch', tabName, {
    context: {
      fromTab: extra?.fromTab,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪筛选操作
 */
export function trackFilter(
  filterName: string,
  filterValue: any,
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
  },
): void {
  collectBehavior('filter', filterName, {
    context: {
      filterValue,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪排序操作
 */
export function trackSort(
  sortField: string,
  sortOrder: 'asc' | 'desc',
  extra?: {
    businessModule?: string;
    context?: Record<string, any>;
  },
): void {
  collectBehavior('sort', sortField, {
    context: {
      sortOrder,
      ...extra?.context,
    },
    ...extra,
  });
}

/**
 * 追踪自定义事件
 */
export function trackCustomEvent(
  actionType: ActionType,
  actionName: string,
  extra?: Parameters<typeof createBehaviorLog>[2],
): void {
  collectBehavior(actionType, actionName, extra);
}

// ========================================================================
// 清理函数
// ========================================================================

/**
 * 清空待上报的行为日志
 */
export function clearPendingBehaviors(): void {
  const count = pendingLogs.length;
  pendingLogs.length = 0;

  if (count > 0) {
    // eslint-disable-next-line no-console
    console.info(`[BehaviorTracker] 已清空 ${count} 条待上报行为日志`);
  }
}

/**
 * 获取待上报日志数量
 */
export function getPendingBehaviorCount(): number {
  return pendingLogs.length;
}

/**
 * 销毁追踪器
 */
export function destroyBehaviorTracker(): void {
  if (reportTimer) {
    clearInterval(reportTimer);
    reportTimer = null;
  }
  removeGlobalClickListener();
  pendingLogs.length = 0;
  currentPageInfo = null;
  lastClickFingerprint = '';
  lastClickTime = 0;
  isInitialized = false;
}
